import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Search, Package2, Filter, DollarSign, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getConditionStyle = (condition) => {
  if (condition === 'new') return 'bg-green-100 text-green-700';
  if (condition === 'like_new') return 'bg-blue-100 text-blue-700';
  return 'bg-yellow-100 text-yellow-700';
};

export default function Marketplace() {
  const { t } = useTranslation();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const observer = useRef();
  const navigate = useNavigate();

  const getConditionLabel = (condition) => {
    const labels = {
      'new': t('marketplace.condition.new', 'New'),
      'like_new': t('marketplace.condition.likeNew', 'Like New'),
      'used': t('marketplace.condition.used', 'Used')
    };
    return labels[condition] || t('marketplace.condition.used', 'Used');
  };

  const lastListingRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setCurrentPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  const loadListings = useCallback(async () => {
    if (currentPage === 1) setLoading(true);
    else setLoadingMore(true);
    
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', '20');
      if (searchTerm) params.append('search', searchTerm);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);

      const response = await axios.get(`${API}/marketplace?${params.toString()}`);
      const newItems = response.data.items || response.data;
      
      if (currentPage === 1) {
        setListings(newItems);
      } else {
        setListings(prev => [...prev, ...newItems]);
      }
      
      setTotalItems(response.data.total || newItems.length);
      setHasMore(currentPage < (response.data.total_pages || 1));
    } catch (error) {
      logger.error('Failed to load marketplace:', error);
      toast.error(t('marketplace.loadError', 'Failed to load marketplace'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentPage, searchTerm, minPrice, maxPrice]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const handleSearch = () => {
    setListings([]);
    setCurrentPage(1);
    setHasMore(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">{t('common.loading', 'Loading...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
            {t('marketplace.title', 'Yarn Marketplace')}
          </h1>
          <p className="text-lg text-muted-foreground font-body">
            {t('marketplace.subtitle', 'Buy and sell yarn from the community')}
          </p>
        </div>

        <div className="mb-8 rounded-3xl bg-white border border-stone-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('marketplace.searchPlaceholder', 'Search by brand, color, or weight...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-12 rounded-xl"
                />
              </div>
            </div>
            <div>
              <Input
                type="number"
                placeholder={t('marketplace.minPrice', 'Min price')}
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="rounded-xl"
              />
            </div>
            <div>
              <Input
                type="number"
                placeholder={t('marketplace.maxPrice', 'Max price')}
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="rounded-xl"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleSearch}
              className="rounded-full bg-primary text-primary-foreground"
            >
              <Filter className="h-4 w-4 mr-2" />
              {t('marketplace.applyFilters', 'Apply Filters')}
            </Button>
          </div>
        </div>

        {listings.length === 0 ? (
          <div className="text-center py-20 rounded-3xl bg-white border border-stone-100">
            <Package2 className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="text-2xl font-heading font-semibold mb-2">{t('marketplace.noListings', 'No listings found')}</h3>
            <p className="text-muted-foreground font-body mb-6">
              {t('marketplace.noListingsHint', 'Try adjusting your search or check back later')}
            </p>
            <Button
              onClick={() => navigate('/inventory')}
              className="rounded-full bg-secondary text-secondary-foreground"
            >
              {t('marketplace.listYourYarn', 'List Your Yarn')}
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {listings.map((yarn, index) => (
                <div
                  key={yarn.yarn_id}
                  ref={index === listings.length - 1 ? lastListingRef : null}
                  className="group relative rounded-3xl overflow-hidden bg-white border-4 border-white shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
                >
                  <div className="aspect-square bg-gradient-to-br from-rose-100 to-orange-100 flex items-center justify-center p-6">
                    <Package2 className="h-16 w-16 text-primary" />
                  </div>
                  <div className="p-4 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-heading font-semibold text-lg truncate flex-1">
                        {yarn.brand || t('marketplace.unknownBrand', 'Unknown Brand')}
                      </h3>
                      <span className="flex items-center text-lg font-bold text-accent ml-2">
                        <DollarSign className="h-4 w-4" />
                        {yarn.sale_price}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate mb-2">
                      {yarn.color || t('marketplace.unknownColor', 'Unknown Color')}
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                      <span className="text-xs font-medium text-stone-500">
                        {yarn.weight || t('marketplace.notAvailable', 'N/A')}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${getConditionStyle(yarn.condition)}`}>
                        {getConditionLabel(yarn.condition)}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/inventory/${yarn.yarn_id}`);
                      }}
                      className="w-full mt-3 rounded-full bg-primary text-primary-foreground"
                    >
                      {t('marketplace.viewDetails', 'View Details')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            {loadingMore && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            
            {!hasMore && listings.length > 0 && (
              <p className="text-center text-muted-foreground py-8">
                {t('marketplace.seenAll', "You've seen all {{count}} listings", { count: totalItems })}
              </p>
            )}
          </>
        )}
      </main>
    </div>
  );
}
